import type { EmodeFlavour } from "@/game-apex/models/event-apex-data.mjs";
import type { ApexSeason } from "@/game-apex/models/seasons.mjs";

export const APEX_PLATFORMS = {
  PC_ORIGIN: 2,
  PC_STEAM: 7,
  PS5: 1,
  SWITCH: 9,
};

export const DEFAULT_MATCHES_LIMIT = 20;

export const OVERLAY_IDS = {
  apexBenchmark: "apexBenchmark",
  apexBenchmarkAd: "apexBenchmarkAd",
  apexLegendSelect: "apexLegendSelect",
  apexLegendSelectAd: "apexLegendSelectAd",
};

export const GAME_MODE_KEYS = [
  "ALL",
  "TRIO",
  "DUO",
  // "QUADS",
  "CAMPFIRE",
  "ARMED_AND_DANGEROUS",
  "RANKED",
  "ARENAS",
  "RANKED_ARENAS",
  "CONTROL",
  "GUNGAME",
  "SHADOWROYALE",
  "WINTER_EXPRESS",
  "HARDCORE_SURVIVAL",
  "FTDM",
  "HEATWAVE",
  "LIVING_SHELL_TRIO",
  "WILDCARD",
] as const;
export type GameMode = (typeof GAME_MODE_KEYS)[number];

export type GameModeMeta = {
  t: Translation;
  tBr?: Translation;

  noLegendStats?: boolean;
  noWepStats?: boolean;
  noPlacement?: boolean;
  hidden?: boolean;
  key: GameMode;
  rankedMode?: GameMode;
  statsMode: GameMode;
  EModeFlavour?: EmodeFlavour; // If a game mode has EmodeFlavour then it will have stats data object in APEX_DATA
};

export const GAME_MODES: Record<GameMode, GameModeMeta> = {
  ALL: {
    t: ["common:allModes", "All Modes"],
    tBr: ["common:allBrModes", "All BR Modes"],
    key: "ALL",
    rankedMode: "RANKED",
    statsMode: "ALL",
  },
  TRIO: {
    t: ["apex:modes.trios", "Trios"],
    key: "TRIO",
    rankedMode: "RANKED",
    statsMode: "ALL",
    noWepStats: true,
    noLegendStats: true,
  },
  DUO: {
    t: ["apex:modes.duo", "Duo"],
    key: "DUO",
    rankedMode: "RANKED",
    statsMode: "ALL",
    noWepStats: true,
    noLegendStats: true,
  },
  // QUADS: {
  //   t: ["apex:modes.quads", "Quads"],
  //   key: "QUADS",
  //   rankedMode: "RANKED",
  //   statsMode: "ALL",
  //   noWepStats: true,
  //   noLegendStats: true,
  // },
  CAMPFIRE: {
    t: ["apex:modes.flashPoint", "Flash Point"],
    hidden: true,
    key: "CAMPFIRE",
    statsMode: "CAMPFIRE",
    noWepStats: true,
    noLegendStats: true,
  },
  ARMED_AND_DANGEROUS: {
    t: ["apex:modes.armedAndDangerous", "Armed and Dangerous"],
    hidden: true,
    key: "ARMED_AND_DANGEROUS",
    statsMode: "ARMED_AND_DANGEROUS",
    EModeFlavour: "armeddangerous",
  },
  RANKED: {
    t: ["apex:modes.rankedLeague", "Ranked League"],
    hidden: false,
    key: "RANKED",
    rankedMode: "RANKED",
    statsMode: "RANKED",
    noWepStats: true,
    noLegendStats: true,
  },
  ARENAS: {
    t: ["apex:modes.arena", "Arena"],
    hidden: true,
    noPlacement: true,
    noWepStats: true, // disabled because areans is no longer in the game
    key: "ARENAS",
    rankedMode: "RANKED_ARENAS",
    statsMode: "ARENAS",
    EModeFlavour: "arenas",
    noLegendStats: true,
  },
  RANKED_ARENAS: {
    t: ["apex:modes.rankedArena", "Ranked Arena"],
    hidden: true,
    noPlacement: true,
    noLegendStats: true,
    noWepStats: true, // disabled because areans is no longer in the game
    key: "RANKED_ARENAS",
    rankedMode: "RANKED_ARENAS",
    statsMode: "RANKED_ARENAS",
  },
  CONTROL: {
    t: ["apex:modes.control", "Control"],
    noPlacement: true,
    key: "CONTROL",
    statsMode: "CONTROL",
    EModeFlavour: "control",
  },
  GUNGAME: {
    t: ["apex:modes.gunRun", "Gun Run"],
    key: "GUNGAME",
    statsMode: "GUNGAME",
    EModeFlavour: "gungame",
  },
  SHADOWROYALE: {
    t: ["apex:modes.shadowRoyale", "Shadow Royale"],
    hidden: true,
    key: "SHADOWROYALE",
    statsMode: "SHADOWROYALE",
    EModeFlavour: "shadowroyale",
  },
  WINTER_EXPRESS: {
    t: ["apex:modes.winterExpress", "Winter Express"],
    hidden: true,
    key: "WINTER_EXPRESS",
    statsMode: "WINTER_EXPRESS",
    EModeFlavour: "winter_express",
  },
  HARDCORE_SURVIVAL: {
    t: ["apex:modes.hardcoreRoyale", "Hardcore Royale"],
    hidden: true,
    key: "HARDCORE_SURVIVAL",
    statsMode: "HARDCORE_SURVIVAL",
    EModeFlavour: "hardcore_survival",
  },
  FTDM: {
    t: ["apex:modes.teamDeathmatch", "Team Deathmatch"],
    key: "FTDM",
    statsMode: "FTDM",
    EModeFlavour: "ftdm",
  },
  HEATWAVE: {
    t: ["apex:modes.heatwave", "Heatwave"],
    hidden: true,
    key: "HEATWAVE",
    statsMode: "HEATWAVE",
    EModeFlavour: "heatwave",
  },
  LIVING_SHELL_TRIO: {
    t: ["apex:modes.livingShell", "Living Shell Trio"],
    hidden: true,
    noWepStats: true,
    noLegendStats: true,
    key: "LIVING_SHELL_TRIO",
    statsMode: "LIVING_SHELL_TRIO",
  },
  WILDCARD: {
    t: ["apex:modes.wildcard", "Wildcard"],
    key: "WILDCARD",
    statsMode: "WILDCARD",
  },
} as const;

export const GAME_MODE_STATS_KEYS = Object.values(GAME_MODES).map(
  (v) => v.statsMode,
);

export const LIFETIME_SEASON: ApexSeason = {
  apexRankedPeriodIds: ["ALL"],
  apexArenaRankedPeriodId: "ALL",
  apexId: "ALL",
  apexSeasonNumber: "0",
  endTime: undefined,
  hasSplitResetOccured: false,
  id: "1",
  startTime: undefined,
};

export const LEGENDS: Record<
  string,
  {
    apexId: string;
    name: string;
    modelNames: string[];
    class: string;
  }
> = {
  said01399802246: {
    apexId: "said01399802246",
    name: "Seer",
    modelNames: ["seer"],
    class: "recon",
  },
  said00088599337: {
    apexId: "said00088599337",
    name: "Horizon",
    modelNames: ["horizon", "nova"],
    class: "skirmisher",
  },
  said00182221730: {
    apexId: "said00182221730",
    name: "Gibraltar",
    modelNames: ["gibraltar"],
    class: "support",
  },
  said00898565421: {
    apexId: "said00898565421",
    name: "Bloodhound",
    modelNames: ["bloodhound"],
    class: "recon",
  },
  said01409694078: {
    apexId: "said01409694078",
    name: "Lifeline",
    modelNames: ["lifeline", "support"],
    class: "support",
  },
  said01464849662: {
    apexId: "said01464849662",
    name: "Pathfinder",
    modelNames: ["pathfinder"],
    class: "skirmisher",
  },
  said00827049897: {
    apexId: "said00827049897",
    name: "Wraith",
    modelNames: ["wraith"],
    class: "skirmisher",
  },
  said00725342087: {
    apexId: "said00725342087",
    name: "Bangalore",
    modelNames: ["bangalore"],
    class: "assault",
  },
  said01111853120: {
    apexId: "said01111853120",
    name: "Caustic",
    modelNames: ["caustic"],
    class: "controller",
  },
  said02045656322: {
    apexId: "said02045656322",
    name: "Mirage",
    modelNames: ["mirage", "holo"],
    class: "support",
  },
  said00843405508: {
    apexId: "said00843405508",
    name: "Octane",
    modelNames: ["stim"],
    class: "skirmisher",
  },
  said00187386164: {
    apexId: "said00187386164",
    name: "Wattson",
    modelNames: ["wattson"],
    class: "controller",
  },
  said00435256162: {
    apexId: "said00435256162",
    name: "Valkyrie",
    modelNames: ["valkyrie", "valk"],
    class: "skirmisher",
  },
  said00216194192: {
    apexId: "said00216194192",
    name: "Ash",
    modelNames: ["ash"],
    class: "assault",
  },
  said00064207844: {
    apexId: "said00064207844",
    name: "Revenant",
    modelNames: ["revenant"],
    class: "assault",
  },
  said00496516687: {
    apexId: "said00496516687",
    name: "Mad Maggie",
    modelNames: ["madmaggie"],
    class: "assault",
  },
  said02105222312: {
    apexId: "said02105222312",
    name: "Rampart",
    modelNames: ["rampart"],
    class: "controller",
  },
  said00080232848: {
    apexId: "said00080232848",
    name: "Crypto",
    modelNames: ["crypto"],
    class: "recon",
  },
  said00405279270: {
    apexId: "said00405279270",
    name: "Fuse",
    modelNames: ["fuse"],
    class: "assault",
  },
  said01579967516: {
    apexId: "said01579967516",
    name: "Loba",
    modelNames: ["loba"],
    class: "support",
  },
  said01014661899: {
    apexId: "said01014661899",
    name: "Newcastle",
    modelNames: ["newcastle"],
    class: "support",
  },
  said00126647596: {
    apexId: "said00126647596",
    name: "Vantage",
    modelNames: ["vantage"],
    class: "recon",
  },
  said00147804686: {
    apexId: "said00147804686",
    name: "Catalyst",
    modelNames: ["catalyst"],
    class: "controller",
  },
  said00596489740: {
    apexId: "said00596489740",
    name: "Ballistic",
    modelNames: ["ballistic"],
    class: "assault",
  },
  said01847866776: {
    apexId: "said01847866776",
    name: "Conduit",
    modelNames: ["conduit"],
    class: "support",
  },
  said00089380567: {
    apexId: "said00089380567",
    name: "Alter",
    modelNames: ["alter"],
    class: "skirmisher",
  },
  said00366578167: {
    apexId: "said00366578167",
    name: "Sparrow",
    modelNames: ["sparrow"],
    class: "skirmisher",
  },
};

export enum MatchTileTabsEnum {
  Scoreboard = "scoreboard",
  Statistics = "statistics",
  Hits = "weaponKills",
  Weapons = "weaponHits",
}
